import { useId } from 'react';
import { BUSINESS_TIME_ZONE } from '@domain/dates';
import { DatePicker } from '@/components/ui/date-picker';
import { MonthPicker } from '@/components/ui/month-picker';
import { Label } from '@/components/ui/label';
import { TEXT } from './typography';

/*
 * Field furniture shared by the two controls below — and the pattern a screen's own fields should follow:
 * a `Label` above, the control, then the hint and the error, each tied to the control by
 * `aria-describedby`.
 */
const FIELD = 'flex flex-col gap-1.5';
const HINT = TEXT.hint;
const ERROR = TEXT.fieldError;

/**
 * A labelled day (2026-09-25: no native `type="date"` is left in the app). The control is the app's date picker
 * (`ui/date-picker.tsx`, a Radix Popover holding a react-day-picker calendar in Vietnamese), which shows the company's
 * dd/MM/yyyy and hands back the ISO `yyyy-MM-dd` the API takes, once per whole day chosen. The field keeps the
 * furniture it always had: a `Label` for the control (`id`, or one of its own), the hint and the error tied to it.
 */
export function DateField({ id: given, label, value, onChange, min, max, required, hint, error }: {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  hint?: string;
  error?: string;
}) {
  const own = useId();
  const id = given ?? own;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={FIELD}>
      <Label htmlFor={id}>{label}</Label>
      <DatePicker
        id={id} label={label} value={value} min={min} max={max} required={required}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
        onChange={onChange}
      />
      {hint && <p className={HINT} id={hintId}>{hint}</p>}
      {error && <p className={ERROR} id={errorId}>{error}</p>}
    </div>
  );
}

/**
 * A labelled month (2026-09-27: no native `type="month"` is left in the app). The control is the app's Radix month
 * picker (`ui/month-picker.tsx`, a Popover holding the twelve months of a year in Vietnamese), which shows MM/YYYY and
 * hands back the `YYYY-MM` the API takes, once per month chosen. The field keeps its furniture: a `Label` for the
 * control, the hint and the error tied to it. `readOnly` rather than `disabled` for a month that can no longer move:
 * the trigger stays focusable and announced (`aria-readonly`), reads muted, and opens nothing.
 */
export function MonthField({ id: given, label, value, onChange, min, max, required, readOnly, hint, error }: {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  readOnly?: boolean;
  hint?: string;
  error?: string;
}) {
  const own = useId();
  const id = given ?? own;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={FIELD}>
      <Label htmlFor={id}>{label}</Label>
      <MonthPicker
        id={id} label={label} value={value} min={min} max={max} required={required} readOnly={readOnly}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
        onChange={onChange}
      />
      {hint && <p className={HINT} id={hintId}>{hint}</p>}
      {error && <p className={ERROR} id={errorId}>{error}</p>}
    </div>
  );
}

/**
 * The company reads dates as dd/mm/yyyy; the API speaks ISO. One place does the turn — and it does it in the
 * business timezone, never the browser's.
 *
 * `new Date('2026-09-21')` is UTC midnight, so a formatter left to the reader's own zone showed the twentieth
 * to everyone west of Greenwich: the deadline a person is chased for, off by one, on a laptop that happened to
 * be travelling. Every date this product holds is a business day in Ho Chi Minh City — the same day the API,
 * the worker and the database already agree on through `businessDate` — so that zone is named here rather
 * than inherited.
 */
const DAY = new Intl.DateTimeFormat('vi-VN', {
  timeZone: BUSINESS_TIME_ZONE, day: '2-digit', month: '2-digit', year: 'numeric',
});

const DAY_AND_TIME = new Intl.DateTimeFormat('vi-VN', {
  timeZone: BUSINESS_TIME_ZONE,
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/**
 * A clock reading is only honest with the clock it belongs to. An audit entry, a timeline moment and a link's
 * expiry are all instants, so they are shown in the business timezone and say which one that is — otherwise
 * a reader abroad reads "07:08" as their own morning and mis-times a reply or an expiring invitation.
 */
const ZONE_LABEL = 'GMT+7';

export function formatDate(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return '—';
  return DAY.format(value);
}

export function formatDateTime(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return '—';
  return `${DAY_AND_TIME.format(value)} (${ZONE_LABEL})`;
}
